import { useState, useEffect, lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { LogOut, LogIn, Search, Menu, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { GlobalSearchDialog } from '@/components/search/GlobalSearchDialog';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { NotificationsPopover } from '@/components/NotificationsPopover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// Lazy: keeps the 105KB userGuides.ts out of the main bundle until Help is opened.
const HelpCenter = lazy(() =>
  import('@/components/help/HelpCenter').then(m => ({ default: m.HelpCenter }))
);

export function AppHeader() {
  const navigate = useNavigate();
  const { user, signOut, displayName, role } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setHelpOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <>
      <header className="sticky top-0 z-40 h-14 border-b border-border bg-background/95 backdrop-blur-xl">
        <div className="flex h-full items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="md:hidden">
              <Menu className="h-5 w-5" />
            </SidebarTrigger>
            
            <div className="hidden md:block">
              <p className="text-sm text-muted-foreground">
                {displayName || user?.email || 'Guest'}
                {role && <span className="ml-1.5 opacity-60">• {role}</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search Button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="hidden sm:flex items-center gap-2 text-muted-foreground"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-4 w-4" />
              <span className="hidden lg:inline">Search...</span>
              <kbd className="hidden lg:inline-flex pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground">
                ⌘K
              </kbd>
            </Button>

            <Button 
              variant="ghost" 
              size="icon" 
              className="sm:hidden h-9 w-9"
              onClick={() => setSearchOpen(true)}
              aria-label="Open search"
            >
              <Search className="h-4 w-4" />
            </Button>

            {/* Help Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-9 w-9"
                  onClick={() => setHelpOpen(true)}
                  aria-label="Open help center"
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Help Center <kbd className="ml-1 text-xs opacity-60">⌘/</kbd></p>
              </TooltipContent>
            </Tooltip>

            <NotificationsPopover />
            <ThemeToggle />

            {user ? (
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleSignOut} aria-label="Sign out">
                <LogOut className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate('/auth')} aria-label="Sign in">
                <LogIn className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      {helpOpen && (
        <Suspense fallback={null}>
          <HelpCenter open={helpOpen} onOpenChange={setHelpOpen} />
        </Suspense>
      )}
    </>
  );
}
